/**
 * TaskStaffingTable.tsx
 * SECTION 1 of the "Staff this job" redesign (2026-07-14,
 * qa/staff-this-job-redesign-2026-07-14/SPEC.md §1 "Tests on this job") — a
 * COMPACT TABLE, one row per test: Test / Dates / Assigned crew (photo-avatar
 * chips + a wordless skill-level badge each) + a "+ add" affordance. Replaces
 * the old TaskStaffingCard list.
 *
 * Explicit per the locked spec: show the skill-LEVEL badge (L3+ green
 * qualified, L1/L2 amber mentor nudge); do NOT show the required-skill name;
 * do NOT show the machine; DO keep the date span. Avatars are real MS Graph
 * photos (photoByEmail, resolved by the parent view), falling back to colored
 * initials. Colors via theme tokens — no hex literals (CODING-STANDARDS).
 */

import React, { useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import SchoolIcon from '@mui/icons-material/School';
import CloseIcon from '@mui/icons-material/Close';
import { alpha, useTheme } from '@mui/material/styles';

import { InitialsAvatar } from '../../../../components/schedule/InitialsAvatar';
import { SkillBadge } from './SkillBadge';
import { taskNeedsMentor } from '../../../../utils/mentorNudge';
import { MENTOR_SKILL_LEVEL } from '../../../../utils/rosterAllocation';
import { AssignPickerPopover } from './AssignPickerPopover';
import { staffAmberPillSx, staffCardSx, staffHair, STAFF_CHIP_RADIUS } from './staffViewStyle';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../../../components/shared/ConfirmDialog';
import { displayTaskName } from './taskDisplayName';
import { rollupPrepTasks, prepBreakdownLabel, unstaffedPrepMembers, type PrepStaffingGap } from './prepRollup';
import { fmtShortDate, saturdayWeekStart } from '../../../../utils/weekUtils';
import type { TaskWithBookings, TaskAssignee } from '../../../../services/scheduleTasks';
import type { UseStaffJobModelResult } from './useStaffJobModel';

export interface TaskStaffingTableProps {
  /** Tasks, already sorted chronologically by the caller. */
  tasks: TaskWithBookings[];
  assignmentMap: Record<string, TaskAssignee[]>;
  model: UseStaffJobModelResult;
  locked: boolean;
  /** email → resolved MS Graph photo blob URL (or null). From useGraphPhotos in the view. */
  photoByEmail?: Map<string, string | null>;
  /**
   * Index staffing (2026-07-28): extra `<TableBody>` content rendered INSIDE
   * the same `<Table>` after the test rows, so the index-allocation rows share
   * the exact column grid (Test / Dates / Tests / Assigned crew).
   */
  extraRows?: React.ReactNode;
}

/** One deduped assigned person on a task row. */
interface RowPerson {
  userKey: string;
  displayName: string;
  photoUrl: string | null;
  level: number | null;
}

function dedupePeople(
  assignees: TaskAssignee[],
  levelFor: (userKey: string) => number | null,
  photoByEmail?: Map<string, string | null>,
): RowPerson[] {
  const seen = new Map<string, RowPerson>();
  for (const a of assignees) {
    const lk = a.UserKey.toLowerCase();
    if (!seen.has(lk)) {
      seen.set(lk, {
        userKey: a.UserKey,
        displayName: a.displayName,
        photoUrl: (a.email ? photoByEmail?.get(a.email) : null) ?? a.photoUrl ?? null,
        level: levelFor(a.UserKey),
      });
    }
  }
  return [...seen.values()].sort((x, y) => x.displayName.localeCompare(y.displayName));
}

/** "W2–W3" chip label + the plain date range, from the project-window week columns.
 *  Exported for the index-allocation rows (2026-07-28) so their Dates cell
 *  reads identically to the test rows'. */
export function weekSpanLabel(task: { StartDate: string | null; EndDate: string | null }, projectWeekColumns: string[]): { weekLabel: string | null; rangeLabel: string | null } {
  const start = task.StartDate?.slice(0, 10);
  const end = task.EndDate?.slice(0, 10);
  if (!start || !end) return { weekLabel: null, rangeLabel: null };
  const rangeLabel = start === end ? fmtShortDate(start) : `${fmtShortDate(start)} – ${fmtShortDate(end)}`;
  const si = projectWeekColumns.indexOf(saturdayWeekStart(start));
  const ei = projectWeekColumns.indexOf(saturdayWeekStart(end));
  if (si === -1 || ei === -1) return { weekLabel: null, rangeLabel };
  const weekLabel = si === ei ? `W${si + 1}` : `W${si + 1}–W${ei + 1}`;
  return { weekLabel, rangeLabel };
}

export const TaskStaffingTable: React.FC<TaskStaffingTableProps> = ({ tasks, assignmentMap, model, locked, photoByEmail, extraRows }) => {
  const theme = useTheme();
  // Pickers act on 1..N tasks: a plain row targets [task]; a consolidated prep
  // row fans out to every member ("two people on prep", not per-test).
  const [picker, setPicker] = useState<{ anchorEl: HTMLElement; targets: TaskWithBookings[] } | null>(null);
  const [mentorPicker, setMentorPicker] = useState<{ anchorEl: HTMLElement; targets: TaskWithBookings[] } | null>(null);
  const [confirm, setConfirm] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  const closeConfirm = () => setConfirm((c) => ({ ...c, open: false }));

  // Prep consolidation (BUG-2026-0165): fold per-line prep rows into one line
  // per prep step. Display + fan-out only — the member tasks are untouched.
  const rows = useMemo(
    () => rollupPrepTasks(tasks).map((entry) => {
      const targets = entry.kind === 'task' ? [entry.task] : entry.group.members;
      const rep = targets[0];
      const skill = model.taskSkill(rep);
      const assignees = targets.flatMap((t) => assignmentMap[t.ScheduleTaskKey] ?? []);
      const people = dedupePeople(assignees, skill.levelFor, photoByEmail);
      const span = entry.kind === 'task'
        ? entry.task
        : { StartDate: entry.group.startDate, EndDate: entry.group.endDate };
      const { weekLabel, rangeLabel } = weekSpanLabel(span, model.projectWeekColumns);
      const needsMentor = taskNeedsMentor(people.map((p) => p.level), skill.skillRequired);
      // BUG-2026-0203: a consolidated prep row shows the UNION of its members'
      // crew, so a step staffed on one test line but not another (a re-sync
      // added a line after staffing) read as fully staffed while the header
      // said "1 unstaffed". Surface the PARTIAL gap on the row; a group with no
      // crew at all keeps the plain "Unstaffed" cue.
      const gap: PrepStaffingGap | null = entry.kind === 'prepGroup' ? unstaffedPrepMembers(entry.group, assignmentMap) : null;
      const partialGap = gap && gap.members.length > 0 && gap.members.length < targets.length ? gap : null;
      return {
        key: entry.kind === 'task' ? entry.task.ScheduleTaskKey : entry.group.groupKey,
        name: entry.kind === 'task' ? displayTaskName(entry.task.Name, entry.task.TaskTypeName) : entry.group.name,
        prepGroup: entry.kind === 'prepGroup' ? entry.group : null,
        qty: entry.kind === 'task' ? entry.task.TestQty : entry.group.totalTests,
        targets, skill, people, weekLabel, rangeLabel, needsMentor, partialGap,
      };
    }),
    [tasks, assignmentMap, model, photoByEmail],
  );

  return (
    <Box sx={staffCardSx(theme)}>
      <Table size="small" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '23%', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', backgroundColor: theme.table.headerBg }}>
              Test
            </TableCell>
            <TableCell sx={{ width: '18%', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', backgroundColor: theme.table.headerBg }}>
              Dates
            </TableCell>
            <TableCell sx={{ width: '9%', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', backgroundColor: theme.table.headerBg }}>
              Tests
            </TableCell>
            <TableCell sx={{ width: '50%', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', backgroundColor: theme.table.headerBg }}>
              Assigned crew
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ key, name, prepGroup, qty, targets, skill, people, weekLabel, rangeLabel, needsMentor, partialGap }) => {
            const unstaffed = people.length === 0;
            const busy = targets.some((t) => model.busyTaskKey === t.ScheduleTaskKey);
            return (
              <TableRow
                key={key}
                sx={{
                  '&:hover td': { backgroundColor: theme.schedule.selectionBg },
                  '& td': { borderBottom: `1px solid ${staffHair(theme)}` },
                  '&:last-of-type td': { borderBottom: 'none' },
                }}
              >
                <TableCell sx={{ py: 1.2, verticalAlign: 'middle' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>{name}</Typography>
                    {prepGroup && (
                      <Tooltip title={`One line per prep step — covers ${prepGroup.members.length} tests: ${prepBreakdownLabel(prepGroup)}. Staffing applies to all of them.`} arrow>
                        <Box component="span" sx={{ fontSize: '0.6rem', fontWeight: 700, color: theme.schedule.machineTime, backgroundColor: alpha(theme.schedule.machineTime, 0.12), borderRadius: '999px', px: 0.7, py: 0.15 }}>
                          {prepGroup.members.length} tests
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 1.2, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                  {weekLabel ? (
                    <Box
                      component="span"
                      sx={{
                        fontSize: '0.68rem', fontWeight: 700, color: theme.schedule.committed,
                        backgroundColor: theme.schedule.selectionBg, border: `1px solid ${staffHair(theme)}`,
                        borderRadius: `${STAFF_CHIP_RADIUS}px`, px: 0.75, py: 0.2, display: 'inline-flex', alignItems: 'baseline', gap: 0.5,
                      }}
                    >
                      {weekLabel}
                      {rangeLabel && <Box component="span" sx={{ fontSize: '0.6rem', fontWeight: 500, color: 'text.secondary' }}>{rangeLabel}</Box>}
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>{rangeLabel ?? '—'}</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ py: 1.2, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                  {qty != null ? (
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.4 }}>
                      <Typography component="span" sx={{ fontSize: '0.9rem', fontWeight: 800, color: theme.schedule.machineTime, lineHeight: 1 }}>
                        {qty}
                      </Typography>
                      <Typography component="span" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                        {qty === 1 ? 'test' : 'tests'}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>—</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ py: 1.05 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25 }}>
                    {unstaffed && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: theme.schedule.maintenance }}>
                        <WarningAmberRoundedIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700 }}>Unstaffed</Typography>
                      </Box>
                    )}
                    {people.map((p) => (
                      <Box
                        key={p.userKey}
                        sx={{
                          position: 'relative', width: 142, flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 0.6,
                          border: `1px solid ${staffHair(theme)}`, borderRadius: '999px',
                          pl: 0.35, pr: 0.7, py: 0.3, backgroundColor: theme.palette.background.paper,
                          '&:hover .rmx': { opacity: 1 },
                        }}
                      >
                        <InitialsAvatar displayName={p.displayName} seed={p.userKey} photoUrl={p.photoUrl} size={20} />
                        <Typography sx={{ flex: 1, minWidth: 0, fontSize: '0.72rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.displayName.split(' ')[0]}
                        </Typography>
                        <SkillBadge level={p.level} skillRequired={skill.skillRequired} variant="compact" size="sm" />
                        {!locked && (
                          <IconButton
                            className="rmx"
                            aria-label={`Remove ${p.displayName.split(' ')[0]} from this test`}
                            onClick={() => setConfirm({
                              open: true,
                              title: prepGroup ? 'Remove from this prep step?' : 'Remove from this test?',
                              message: prepGroup
                                ? `Remove ${p.displayName.split(' ')[0]} from ${name}? This covers all ${prepGroup.members.length} tests' prep; they stay on the job's other work.`
                                : `Remove ${p.displayName.split(' ')[0]} from ${name}? They stay on the job's other tests.`,
                              confirmLabel: 'Remove',
                              confirmColor: 'error',
                              onConfirm: () => { for (const t of targets) model.removeFromTask(t.ScheduleTaskKey, p.userKey); },
                            })}
                            sx={{
                              position: 'absolute', top: -7, right: -7, p: 0.15, opacity: 0, transition: 'opacity .12s',
                              backgroundColor: theme.palette.background.paper, border: `1px solid ${staffHair(theme)}`, color: 'text.secondary',
                              '&:hover': { backgroundColor: alpha(theme.schedule.heatmapHigh, 0.12), color: theme.schedule.heatmapHigh },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 11 }} />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                    {partialGap && (
                      <Tooltip
                        title={`Nobody is on the ${name} for ${partialGap.labels.join(', ')} — the crew shown covers only ${targets.length - partialGap.members.length} of ${targets.length} tests. ${locked ? '' : 'Click to staff the missing one' + (partialGap.members.length === 1 ? '' : 's') + '.'}`.trim()}
                        arrow
                      >
                        <Box
                          role="button"
                          tabIndex={0}
                          aria-label={`${partialGap.members.length} of ${targets.length} unstaffed — ${partialGap.labels.join(', ')}`}
                          onClick={(e) => { if (!locked) setPicker({ anchorEl: e.currentTarget, targets: partialGap.members }); }}
                          onKeyDown={(e) => { if (!locked && (e.key === 'Enter' || e.key === ' ')) setPicker({ anchorEl: e.currentTarget, targets: partialGap.members }); }}
                          sx={staffAmberPillSx(theme)}
                        >
                          <WarningAmberRoundedIcon sx={{ fontSize: 13 }} />
                          <Typography sx={{ fontSize: '0.66rem', fontWeight: 700 }}>
                            {partialGap.members.length} of {targets.length} unstaffed
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}
                    {needsMentor && (
                      <Box
                        role="button"
                        tabIndex={0}
                        onClick={(e) => setMentorPicker({ anchorEl: e.currentTarget, targets })}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setMentorPicker({ anchorEl: e.currentTarget, targets }); }}
                        title="A junior (L1/L2) is on this test — add an L3+ mentor"
                        sx={staffAmberPillSx(theme)}
                      >
                        <SchoolIcon sx={{ fontSize: 13 }} />
                        <Typography sx={{ fontSize: '0.66rem', fontWeight: 700 }}>Needs mentor</Typography>
                      </Box>
                    )}
                    {!locked && (
                      <Tooltip title={prepGroup ? `Assign someone to ${name} — covers all ${prepGroup.members.length} tests' prep` : 'Assign someone to this test'} arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => setPicker({ anchorEl: e.currentTarget, targets })}
                          disabled={busy}
                          sx={{
                            color: theme.schedule.employeeAlloc,
                            border: `1px dashed ${alpha(theme.schedule.employeeAlloc, 0.6)}`,
                            borderRadius: '999px', px: 0.7, py: 0.1,
                            '&:hover': { backgroundColor: alpha(theme.schedule.employeeAlloc, 0.1) },
                          }}
                        >
                          <PersonAddAlt1RoundedIcon sx={{ fontSize: 14 }} />
                          <Typography component="span" sx={{ fontSize: '0.62rem', fontWeight: 700, ml: 0.3 }}>add</Typography>
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
        {extraRows}
      </Table>

      <AssignPickerPopover
        anchorEl={picker?.anchorEl ?? null}
        candidates={picker ? model.candidatesForTask(picker.targets[0]) : []}
        skillRequired={picker ? model.taskSkill(picker.targets[0]).skillRequired : false}
        onPick={(userKey) => {
          // A consolidated prep row fans the assignment out to every member.
          if (picker) for (const t of picker.targets) model.assignToTask(t, userKey);
          setPicker(null);
        }}
        onClose={() => setPicker(null)}
      />

      {/* Mentor picker — all eligible L3+ mentors for the task (not a single name). */}
      <AssignPickerPopover
        anchorEl={mentorPicker?.anchorEl ?? null}
        candidates={mentorPicker
          ? model.candidatesForTask(mentorPicker.targets[0]).filter((c) => typeof c.level === 'number' && c.level >= MENTOR_SKILL_LEVEL)
          : []}
        skillRequired
        onPick={(userKey) => {
          if (mentorPicker) for (const t of mentorPicker.targets) model.addMentorToTask(t, userKey);
          setMentorPicker(null);
        }}
        onClose={() => setMentorPicker(null)}
      />

      <ConfirmDialog state={confirm} onClose={closeConfirm} />
    </Box>
  );
};
